import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { initiateGoogleAuth, signInWithEmail as signInWithEmailService } from '@/services/auth';

import { IAuthProviderProps } from '@/types/auth';

import AuthContext from './AuthContext';
import { useCurrentUser } from './useCurrentUser';

export function AuthProvider({ children }: IAuthProviderProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(
    typeof window !== 'undefined' ? localStorage.getItem('authToken') : null,
  );
  const [signInError, setSignInError] = useState<string | null>(null);
  const prevTokenRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const prev = prevTokenRef.current;
    if (prev !== undefined && prev !== null && token === null) {
      queryClient.clear();
    }
    prevTokenRef.current = token;
  }, [token, queryClient]);

  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem('authToken');
      setToken(newToken);
    };

    window.addEventListener('storage', handleStorageChange);

    handleStorageChange();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const {
    data: userData,
    isLoading: verifyLoading,
    error: verifyError,
    refetch: checkSession,
  } = useCurrentUser(token);

  const user = useMemo(
    () => (userData && token ? { ...userData, token } : null),
    [userData, token],
  );
  const loading = verifyLoading;
  const error = verifyError?.message || signInError || null;

  useEffect(() => {
    if (verifyError && token) {
      localStorage.removeItem('authToken');
      // oxlint-disable-next-line react/set-state-in-effect
      setToken(null);
    }
  }, [verifyError, token]);

  const signIn = useCallback(async (returnUrl?: string) => {
    try {
      setSignInError(null);
      initiateGoogleAuth(returnUrl);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to sign in. Please try again.';
      setSignInError(errorMessage);
    }
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    try {
      setSignInError(null);
      const newToken = await signInWithEmailService(email, password);
      localStorage.setItem('authToken', newToken);
      setToken(newToken);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid email or password';
      setSignInError(errorMessage);
      throw err;
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('authToken');
    queryClient.clear();
    setToken(null);
    void navigate('/');
  }, [queryClient, navigate]);

  const handleCheckSession = useCallback(async () => {
    await checkSession();
  }, [checkSession]);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      signIn,
      signInWithEmail,
      signOut,
      checkSession: handleCheckSession,
    }),
    [user, loading, error, signIn, signInWithEmail, signOut, handleCheckSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
